import React from "react";
import { Box } from "@mui/material";
import { inServiceTypeOrder } from "./opportunityOptions";

// Display helpers shared by the opportunity list, details modal, dashboard and Search dialog.

// A lead/owner may arrive populated ({ firstName, lastName, isArchived }), as a bare id, or as null
// when the User was deleted. Only a populated person has a name to show.
export const formatPersonName = (person) => {
  if (!person || typeof person !== "object") return "—";
  const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
  if (!name) return "—";
  return person.isArchived ? `${name} (inactive)` : name;
};

// Fixed term: whole months from start to end, counting the end day (Feb 1 - Jul 31 is 6 Months).
// Existing and ongoing engagements have no end; records from before 2c have no duration.
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
export const formatDuration = (opportunity) => {
  const kind = opportunity?.engagementDuration;
  if (!kind) return "—";
  if (kind !== "Fixed Term Project") return "Ongoing";
  const start = new Date(opportunity.expectedStartDate);
  const end = new Date(opportunity.expectedEndDate);
  if (!opportunity.expectedStartDate || !opportunity.expectedEndDate || isNaN(start) || isNaN(end) || end < start) return "—";
  const after = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate() + 1));
  let months = (after.getUTCFullYear() - start.getUTCFullYear()) * 12 + after.getUTCMonth() - start.getUTCMonth();
  if (after.getUTCDate() < start.getUTCDate()) months -= 1;
  if (months < 1) return "Less than 1 Month";
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return [years ? plural(years, "Year") : "", rest ? plural(rest, "Month") : ""].filter(Boolean).join(" ");
};

// YYYY-MM-DD in UTC.
export const formatDate = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
};

// `outlined`: white with a dark outline, as Figma draws the Type pill.
export const PillTag = ({ children, outlined = false, ...props }) => (
  <Box
    component="span"
    {...props}
    sx={{
      display: "inline-block",
      px: 1.5,
      py: 0.5,
      borderRadius: "16px",
      border: "1px solid",
      borderColor: outlined ? "#483C3B" : "#9E9E9E",
      backgroundColor: outlined ? "#FFFFFF" : "#F5F5F5",
      fontSize: "0.8125rem",
      color: "text.primary",
    }}
  >
    {children}
  </Box>
);

// Delivery model as a grey pill; "—" for records from before 2c.
export const DeliveryModelCell = ({ opportunity }) =>
  opportunity?.deliveryModel ? <PillTag>{opportunity.deliveryModel}</PillTag> : "—";

// Service types as outlined pills, at most `max`, then "+N" naming the rest.
export const ServiceTypesCell = ({ opportunity, max = 2 }) => {
  const types = inServiceTypeOrder(opportunity?.serviceTypes);
  if (!types.length) return "—";
  const rest = types.slice(max);
  return (
    <Box component="span" sx={{ display: "inline-flex", flexWrap: "wrap", gap: 0.5 }}>
      {types.slice(0, max).map((t) => (
        <PillTag key={t} outlined>{t}</PillTag>
      ))}
      {rest.length > 0 && (
        <PillTag outlined title={rest.join(", ")} aria-label={`${rest.length} more: ${rest.join(", ")}`}>
          +{rest.length}
        </PillTag>
      )}
    </Box>
  );
};
